import * as THREE from 'three/webgpu';

/**
 * Collapse an arena export's static meshes into one mesh per look.
 *
 * The Blender exports arrive as one mesh per part (49–508 of them), each with
 * its own material, and the batch builders keep it that way on purpose: the
 * review tools and their tests address artwork by `partId`. The live game does
 * not. On a phone every one of those meshes is a draw call and a node-material
 * build, and the per-part cost is what made a mesh arena hitch on first sight
 * and crawl after. The exports only carry 12–22 distinct colours, so grouping
 * by look brings an arena down to a couple of dozen draws.
 *
 * Only plain toon/basic parts merge. Anything a controller drives afterwards
 * (HFT belts, arrows and hazards carry `hftRole`), anything with a custom
 * shader (the pit's lava), and anything not shaped like an export batch stays
 * as it was, so behaviour never depends on this pass having run.
 *
 * Runs on a detached group, before it is added to the scene.
 */
const MERGEABLE = new Set(['MeshToonNodeMaterial', 'MeshBasicNodeMaterial']);

function lookKey(mesh) {
  const m = mesh.material;
  if (!mesh.isMesh || mesh.isInstancedMesh || mesh.isSkinnedMesh) return null;
  if (!m || Array.isArray(m) || !MERGEABLE.has(m.type)) return null;
  if (mesh.userData.hftRole || m.positionNode || m.colorNode || m.map) return null;
  const g = mesh.geometry;
  if (!g || g.index || !g.attributes.position || !g.attributes.normal || Object.keys(g.attributes).length !== 2) return null;
  if (mesh.children.length) return null;
  // The raw linear floats, not getHexString(): that goes through sRGB and
  // clamps to 8 bits, so two emissive parts brighter than 1.0 would share a
  // key and one would be drawn in the other's colour.
  const rgb = (c) => (c ? `${c.r},${c.g},${c.b}` : '');
  return [
    m.type,
    rgb(m.color),
    rgb(m.emissive),
    m.emissiveIntensity ?? '',
    m.transparent ? m.opacity : 1,
    m.side,
    mesh.castShadow ? 1 : 0,
    mesh.receiveShadow ? 1 : 0,
  ].join('|');
}

/**
 * One pass per look: each part's vertices are written straight into the
 * merged arrays with its transform applied. Cloning, baking and then
 * mergeGeometries would copy the whole arena twice more on the main thread,
 * in the load path the packed format exists to make cheap.
 */
function mergeInto(meshes) {
  let count = 0;
  for (const mesh of meshes) count += mesh.geometry.attributes.position.count;
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const v = new THREE.Vector3();
  const normalMatrix = new THREE.Matrix3();
  let o = 0;
  for (const mesh of meshes) {
    mesh.updateMatrix();
    normalMatrix.getNormalMatrix(mesh.matrix);
    const p = mesh.geometry.attributes.position;
    const n = mesh.geometry.attributes.normal;
    for (let i = 0; i < p.count; i++, o += 3) {
      v.fromBufferAttribute(p, i).applyMatrix4(mesh.matrix);
      positions[o] = v.x; positions[o + 1] = v.y; positions[o + 2] = v.z;
      v.fromBufferAttribute(n, i).applyMatrix3(normalMatrix).normalize();
      normals[o] = v.x; normals[o + 1] = v.y; normals[o + 2] = v.z;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  return geometry;
}

/** Returns how many meshes were folded away. */
export function mergeStaticArena(root) {
  const groups = new Map();
  for (const mesh of root.children) {
    const key = lookKey(mesh);
    if (!key) continue;
    let list = groups.get(key);
    if (!list) groups.set(key, (list = []));
    list.push(mesh);
  }

  let folded = 0;
  for (const meshes of groups.values()) {
    if (meshes.length < 2) continue;
    const keep = meshes[0];
    const material = keep.material;
    const mesh = keep.clone(false);
    mesh.geometry = mergeInto(meshes);
    mesh.material = material;
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.scale.set(1, 1, 1);
    mesh.name = `Arena merged / ${meshes.length} parts`;
    delete mesh.userData.partId;
    mesh.userData.ownsMaterial = true;

    for (const old of meshes) {
      root.remove(old);
      old.geometry.dispose();
      if (old.material !== material) old.material.dispose();
    }
    root.add(mesh);
    folded += meshes.length - 1;
  }
  return folded;
}
